import React from "react";
import { Link } from "react-router-dom";
import { MarketingLayout } from "../../components/marketing/MarketingLayout";
import { HairlineList } from "../../components/marketing/HairlineList";
import { AccessGrid } from "../../components/marketing/AccessGrid";
import { MechanismCards } from "../../components/marketing/MechanismCards";
import { HeroVideo } from "../../components/marketing/HeroVideo";
import { SovereignWorldMap } from "../../components/marketing/SovereignWorldMap";
import { Accordion } from "../../components/marketing/Accordion";
import { CohereBand } from "../../components/marketing/CohereBand";
import {
  ChapterCover, ChapterMeta, PullQuote, CountUp, productTint,
} from "../../components/marketing/EditorialPolish";

/** Cohere-style colourful band. Flip to false to remove it instantly. */
const SHOW_COLOUR_BAND = true;
/** Editorial polish for Ch.01–Ch.04 (cover motifs, pull-quotes, tints, counters). */
const SHOW_EDITORIAL_POLISH = true;

const HERO_HEADLINE = "You do not have to give up your data to use AI in your organisation.";

const STACK_ROWS = [
  { name: "Atom", desc: "The specialist model family that stays home. Intelligence trained for your work, running where your data lives." },
  { name: "Foundation", desc: "The governed platform that resolves your records into the single true entity behind them, then reasons over the relationships, not just the documents." },
  { name: "Crowther Cloud", desc: "The sovereign cloud beneath all of it. Provisioning, serving, and deployment, in-country, under your regulator's authority." },
  { name: "Realloc", desc: "The workforce platform that measures what intelligence elevates, so every deployment adds to human work rather than replacing it." },
];

const LEDGER_ROWS = [
  ["Nigeria", "Foundation"],
  ["Ghana", "Foundation"],
  ["Kenya", "Realloc"],
  ["South Africa", "Cloud"],
  ["Egypt", "Foundation"],
  ["United Arab Emirates", "Realloc"],
  ["India", "Cloud"],
  ["Indonesia", "Atom"],
  ["Singapore", "Foundation"],
  ["Jamaica", "Realloc"],
  ["Barbados", "Realloc"],
  ["Trinidad and Tobago", "Realloc"],
  ["Brazil", "Realloc"],
  ["Mexico", "Cloud"],
  ["United Kingdom", "Foundation"],
];

const FAQ = [
  { q: "Who holds the keys?", a: "You do. Deployments run inside your own environment, and the encryption keys are held by you. Access is something you grant and revoke." },
  { q: "Does my data train your models?", a: "No. Your data stays inside your environment and is not used to train any model. Atom is deployed to serve your work, not to learn from it for anyone else." },
  { q: "Can it run in-country, or air-gapped?", a: "Yes. The stack is deployed where your data must stay, in your region, under your regulator's authority, and can run disconnected where that is required." },
  { q: "What does my regulator see?", a: "Exactly where your data resides and who has access to it. Foundation retains the full audit trail, so when your regulator asks, you can prove it." },
  { q: "What happens when an agent misbehaves?", a: "It is contained. Access is denied by default and every action is recorded, so a workload can be isolated or revoked without disturbing the systems around it." },
  { q: "Do we have to rebuild our systems to onboard?", a: "No. Crowther connects to your existing systems of record rather than replacing them, and reads from them under the access you define." },
];

const CLOSE_ROWS = [
  { name: "Exposure", desc: "Where your institution's data leaves your control today, mapped to your region and your regulator." },
  { name: "Readiness", desc: "Where you are already positioned to deploy sovereign intelligence, and where the gaps are." },
  { name: "The path", desc: "A concrete sequence to bring intelligence in-country without giving up your data, in one report." },
];

/** Numbered index. Reused across every section to signal editorial flow. */
function SectionIndex({ n, label }) {
  return (
    <div className="mkt-idx" data-testid={`mkt-idx-${n}`}>
      <span className="mkt-idx-num">{n}</span>
      <span className="mkt-idx-rule" />
      <span className="mkt-idx-label">{label}</span>
    </div>
  );
}

export default function Home() {
  return (
    <MarketingLayout
      active="home"
      title="Crowther. Sovereign intelligence, in-country."
      description="You do not have to give up your data to use AI in your organisation. Crowther builds and owns the whole stack beneath your intelligence, deployed where your data must stay."
    >
      {/* Anthropic-style hero: bold sans left with underlined words, serif right,
          CTA + editorial index strip below on a hairline. */}
      <section className="mkt-hero-banner" data-testid="mkt-hero-banner">
        <div className="mkt-hero-banner-grid">
          <div className="mkt-hero-banner-lead">
            <div className="mkt-eyebrow" data-testid="mkt-hero-eyebrow">Human Insight, Amplified.</div>
            <h1 className="mkt-hero-banner-title" data-testid="mkt-hero-banner-title">
              Your own AI, on infrastructure you control.
            </h1>
            <p className="mkt-hero-banner-subtitle" data-testid="mkt-hero-banner-subtitle">
              In your region. Under your governance. Your data stays inside your walls.
            </p>
            <div className="mkt-hero-banner-actions">
              <Link to="/plan" className="mkt-btn mkt-btn-primary" data-testid="mkt-hero-cta">
                Get Your Firm&rsquo;s Free Customised AI Roadmap
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
              <Link to="/enterprise/solution" className="mkt-btn mkt-btn-link">
                What we build
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            </div>
          </div>
          <div className="mkt-hero-banner-side">
            <HeroVideo />
          </div>
        </div>
      </section>

      {/* Anthropic-style dark rounded feature card below the hero */}
      <section className="mkt-hero-feature" data-testid="mkt-hero-feature">
        <div className="eyebrow">The Crowther AI Strategy Roadmap</div>
        <h2 className="title">Crowther is built on hard questions.</h2>
        <p className="sub">
          Where does your data live today? What must stay inside your borders? Who holds the keys when the regulator asks? Answer thirteen of these in five minutes and receive a customised plan showing what to deploy, what to hold, and what to start now.
        </p>
        <div className="cta">
          <Link to="/plan" className="mkt-btn" data-testid="mkt-hero-feature-cta">
            Get Your Firm&rsquo;s Free Customised AI Roadmap
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
        </div>
      </section>

      {/* Legacy editorial hero (hidden via CSS; kept in DOM so testids remain) */}
      <section className="mkt-hero" data-testid="mkt-hero" aria-hidden="true">
        <div className="mkt-eyebrow mkt-hero-eyebrow">Human Insight, Amplified.</div>
        <h1 className="mkt-hero-title" data-testid="mkt-hero-headline">
          Your own AI, on infrastructure you control.
        </h1>
        <p className="mkt-hero-title-sub" data-testid="mkt-hero-subline">
          In your region. Under your governance. Your data stays inside your walls.
        </p>
        <p className="mkt-hero-copy">
          Every serious AI system asks you to send your data somewhere you do not control. Crowther does not. We build the whole stack beneath your intelligence, in-country, on infrastructure you own, and hand it over for you to run.
        </p>
        <div className="mkt-hero-actions">
          <Link to="/plan" className="mkt-btn mkt-btn-primary" data-testid="mkt-hero-cta">
            Get Your Firm&rsquo;s Free Customised AI Roadmap
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
          <Link to="/enterprise/solution" className="mkt-btn mkt-btn-link">
            What we build
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
        </div>

        {/* Editorial index strip: what the plan returns, at a glance */}
        <div className="mkt-hero-index" aria-label="What the plan returns">
          <div className="mkt-hero-index-item">
            <span className="k">The Plan</span>
            <span className="v">Customised.</span>
            <span className="n">Written for your firm, not a template.</span>
          </div>
          <div className="mkt-hero-index-item">
            <span className="k">Format</span>
            <span className="v">13 questions.</span>
            <span className="n">Five minutes from start to answer.</span>
          </div>
          <div className="mkt-hero-index-item">
            <span className="k">Delivery</span>
            <span className="v">Your inbox.</span>
            <span className="n">The moment you finish.</span>
          </div>
          <div className="mkt-hero-index-item">
            <span className="k">Price</span>
            <span className="v">Free.</span>
            <span className="n">Nothing to pay, nothing owed.</span>
          </div>
        </div>
      </section>

      {/* What the plan returns: three tan release cards */}
      <section className="mkt-shell mkt-section" data-testid="mkt-plan-returns">
        <SectionIndex n="01" label="What the plan returns" />
        <div className="mkt-lede">
          <p className="mkt-h-l" style={{ maxWidth: "20ch" }}>
            Three answers, delivered to your inbox.
          </p>
        </div>
        <div className="mkt-releases-grid">
          {/* Feature tile: dark, with big display serif */}
          <div className="mkt-release-card feature" data-testid="mkt-release-feature">
            <div className="kicker">The plan</div>
            <div className="display">
              Your firm.<br />
              Your data.<br />
              Your ninety<br />days.
            </div>
            <p className="desc">
              A customised report showing where AI can grow your revenue, where it can cut your costs, and what to start now. Thirteen questions. Five minutes. Free.
            </p>
            <div className="meta"><span>Format</span><span className="v">Branded PDF</span></div>
            <div className="meta"><span>Delivery</span><span className="v">Your inbox, within the hour</span></div>
            <Link to="/plan" className="cta" data-testid="mkt-release-feature-cta">
              Get Your Firm&rsquo;s Free Customised AI Roadmap
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>

          <div className="mkt-release-card tan" data-testid="mkt-release-exposure">
            <div className="title">Where your data is exposed</div>
            <p className="desc">
              Every point at which your firm&rsquo;s records leave your control today, mapped to your region and your regulator.
            </p>
            <div className="meta"><span>Section</span><span className="v">Exposure</span></div>
            <Link to="/plan" className="cta" data-testid="mkt-release-exposure-cta">
              Read more
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>

          <div className="mkt-release-card mint" data-testid="mkt-release-readiness">
            <div className="title">Where you can deploy now</div>
            <p className="desc">
              A ranked view of the AI moves your firm is already positioned to make. What is production-ready in your environment, and what needs to wait.
            </p>
            <div className="meta"><span>Section</span><span className="v">Readiness</span></div>
            <Link to="/plan" className="cta" data-testid="mkt-release-readiness-cta">
              Read more
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* ================================================================
          CORE — cohesive editorial band that runs from the argument through
          real deployments. Numbered chapters, technical rules, FIG labels.
          ================================================================ */}
      <div className={`mkt-core ${SHOW_EDITORIAL_POLISH ? "mkt-core-tinted" : ""}`} data-testid="mkt-core">
        <div className="mkt-shell mkt-core-head">
          <div className="mkt-core-tag">§&nbsp;&nbsp;CORE&nbsp;&nbsp;·&nbsp;&nbsp;THE&nbsp;ARGUMENT&nbsp;THROUGH&nbsp;THE&nbsp;DEPLOYMENTS</div>
          <div className="mkt-core-rule" />
        </div>

        {/* 02 · The argument — editorial two-column with numbered supporting facts */}
        <section className="mkt-shell mkt-section mkt-core-section" data-testid="mkt-argument">
          {SHOW_EDITORIAL_POLISH && <ChapterCover motif="argument" />}
          <div className="mkt-core-index">
            <span className="k">CH.</span>
            <span className="n">01</span>
            <span className="d">/</span>
            <span className="t">04</span>
            <span className="l">THE ARGUMENT</span>
          </div>
          {SHOW_EDITORIAL_POLISH && (
            <ChapterMeta items={["3 min read", "0 figures", "3 stats"]} />
          )}
          <div className="mkt-arg-grid">
            <div className="mkt-arg-lead">
              <h2 className="mkt-h-xl mkt-arg-title">
                Sovereignty is becoming the price of participation.
              </h2>
              <p className="mkt-arg-lede">
                To use frontier intelligence, you were told your data had to leave. It had to sit on infrastructure you do not own, in a country that is not yours, under rules you did not write.
              </p>
            </div>
            <div className="mkt-arg-facts">
              <div className="mkt-arg-fact">
                <div className="mkt-arg-fact-n">01</div>
                <div className="mkt-arg-fact-b">
                  <div className="v">
                    {SHOW_EDITORIAL_POLISH ? <CountUp value="150+" /> : "150+"}
                  </div>
                  <div className="k">Countries with data residency rules in force</div>
                </div>
              </div>
              <div className="mkt-arg-fact">
                <div className="mkt-arg-fact-n">02</div>
                <div className="mkt-arg-fact-b">
                  <div className="v">A handful</div>
                  <div className="k">Countries where the world&rsquo;s frontier models actually sit</div>
                </div>
              </div>
              <div className="mkt-arg-fact">
                <div className="mkt-arg-fact-n">03</div>
                <div className="mkt-arg-fact-b">
                  <div className="v">Your borders</div>
                  <div className="k">Where sensitive data must now stay by law</div>
                </div>
              </div>
            </div>
          </div>
          <div className="mkt-arg-close">
            <p className="mkt-arg-close-line">
              Crowther closes that gap on your own terms. We build and own the whole stack beneath your intelligence, and we deploy it where your data must stay.
            </p>
          </div>
        </section>

        {SHOW_EDITORIAL_POLISH && (
          <PullQuote mark="§">
            The argument is not against AI. It is against renting it.
          </PullQuote>
        )}

        {/* 03 · Deny by default */}
        <section className="mkt-shell mkt-section mkt-core-section" data-testid="mkt-deny">
          {SHOW_EDITORIAL_POLISH && <ChapterCover motif="deny" />}
          <div className="mkt-core-index">
            <span className="k">CH.</span>
            <span className="n">02</span>
            <span className="d">/</span>
            <span className="t">04</span>
            <span className="l">DENY BY DEFAULT</span>
          </div>
          {SHOW_EDITORIAL_POLISH && (
            <ChapterMeta items={["2 min read", "1 figure", "4 access rules"]} />
          )}
          <div className="mkt-split">
            <div className="mkt-split-title-col">
              <h2 className="mkt-h-l">Your keys.<br />Your data.<br />Access you grant<br />and revoke.</h2>
              <p className="mkt-body mkt-split-lede">
                Every model, agent, and person reaches exactly the systems they need and nothing more. Access is granted and revoked by you, and your keys stay your own.
              </p>
            </div>
            <div>
              <div className="mkt-fig-cap code">FIG.1 &nbsp;·&nbsp; Access surface</div>
              <AccessGrid />
            </div>
          </div>
        </section>

        {/* 04 · The stack we build (formerly split into "what we build" + "mechanism") */}
        <section className="mkt-shell mkt-section mkt-core-section" data-testid="mkt-stack">
          {SHOW_EDITORIAL_POLISH && <ChapterCover motif="stack" />}
          <div className="mkt-core-index">
            <span className="k">CH.</span>
            <span className="n">03</span>
            <span className="d">/</span>
            <span className="t">04</span>
            <span className="l">THE STACK</span>
          </div>
          {SHOW_EDITORIAL_POLISH && (
            <ChapterMeta items={["4 min read", "2 figures", "4 products"]} />
          )}
          <div className="mkt-lede">
            <p className="mkt-h-l" style={{ maxWidth: "16ch" }}>
              One stack, deployed where the data must stay.
            </p>
            <p className="mkt-body mkt-body-lg" style={{ maxWidth: "60ch", marginTop: 24, color: "var(--forest-soft)" }}>
              Each part is a piece of the answer to how you keep your data and still run real AI.
            </p>
          </div>
          <div className="mkt-fig-cap code" style={{ marginTop: 40 }}>FIG.2 &nbsp;·&nbsp; The Crowther stack, top to bottom</div>
          <div style={{ marginTop: 8 }}>
            <HairlineList
              rows={STACK_ROWS.map((r) => ({
                ...r,
                cta: (
                  <Link to="/enterprise/solution" className="mkt-row-cta">
                    Read more
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </Link>
                ),
              }))}
            />
          </div>

          {/* Mechanism cards folded into the same chapter */}
          <div className="mkt-arg-close" style={{ marginTop: 80 }}>
            <div className="mkt-core-sub">The control surface, not a slide</div>
            <p className="mkt-arg-close-line">
              What Foundation resolves, how access is governed, and where a deployment is built to run.
            </p>
          </div>
          <div className="mkt-fig-cap code" style={{ marginTop: 32 }}>FIG.3 &nbsp;·&nbsp; The mechanism</div>
          <div style={{ marginTop: 8 }}>
            <MechanismCards />
          </div>
        </section>

        {SHOW_EDITORIAL_POLISH && (
          <PullQuote mark="§">
            You cannot govern what you do not run.
          </PullQuote>
        )}

        {/* 05 · Deployments — final chapter, sealed with the world map */}
        <section className="mkt-shell mkt-section mkt-core-section" data-testid="mkt-deployments">
          {SHOW_EDITORIAL_POLISH && <ChapterCover motif="deployments" />}
          <div className="mkt-core-index">
            <span className="k">CH.</span>
            <span className="n">04</span>
            <span className="d">/</span>
            <span className="t">04</span>
            <span className="l">DEPLOYMENTS</span>
          </div>
          {SHOW_EDITORIAL_POLISH && (
            <ChapterMeta items={["3 min read", "1 atlas", "19 countries"]} />
          )}
          <div className="mkt-deploy-grid">
            <div className="mkt-deploy-lead">
              <h2 className="mkt-h-l">Real deployments,<br />where the institutions operate.</h2>
              <p className="mkt-body mkt-split-lede">
                Realloc runs across Sagicor operations in the Caribbean, North and Central America. Beyond the anchor programmes, Atom, Foundation and Cloud are live inside institutions across four continents, every one of them running in-region, on the client&rsquo;s own ground.
              </p>
              <div className="mkt-deploy-stats">
                <div className="mkt-deploy-stat">
                  <div className="v">{SHOW_EDITORIAL_POLISH ? <CountUp value="19" /> : "19"}</div>
                  <div className="k">Countries live</div>
                </div>
                <div className="mkt-deploy-stat">
                  <div className="v">{SHOW_EDITORIAL_POLISH ? <CountUp value="4" /> : "4"}</div>
                  <div className="k">Products in the field</div>
                </div>
                <div className="mkt-deploy-stat">
                  <div className="v">{SHOW_EDITORIAL_POLISH ? <CountUp value="4" /> : "4"}</div>
                  <div className="k">Continents</div>
                </div>
              </div>
            </div>
            <div className="mkt-deploy-map">
              <SovereignWorldMap />
              <div className="mkt-ledger" data-testid="mkt-deployment-ledger">
                <div className="mkt-ledger-head">
                  <span>Deployment ledger</span>
                  <span>by product</span>
                </div>
                {LEDGER_ROWS.map(([place, product]) => (
                  <div key={place} className="mkt-ledger-row">
                    <span className="place">{place}</span>
                    <span className={`product ${SHOW_EDITORIAL_POLISH ? productTint(product) : ""}`}>{product}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className="mkt-shell mkt-core-foot">
          <div className="mkt-core-rule" />
          <div className="mkt-core-tag mkt-core-tag-end">§&nbsp;&nbsp;END&nbsp;OF&nbsp;CORE</div>
        </div>
      </div>

      {/* Ask Atom removed per copy update — section 02 deprecated. */}

      {/* Origin — full-bleed serif statement */}
      <section className="mkt-shell mkt-section" data-testid="mkt-origin">
        <SectionIndex n="03" label="Built for the region it serves" />
        <p className="mkt-h-m" style={{ maxWidth: "26ch", fontStyle: "italic" }}>
          A hyperscaler can place a data centre in your country. It cannot be of your country, answerable to your regulator, owned where your data lives.
        </p>
        <p className="mkt-h-m" style={{ maxWidth: "26ch", marginTop: 8 }}>
          Crowther can.
        </p>
      </section>

      {/* Due diligence */}
      <section className="mkt-shell mkt-section" data-testid="mkt-faq">
        <SectionIndex n="04" label="Due diligence" />
        <div className="mkt-lede">
          <p className="mkt-h-l" style={{ maxWidth: "18ch" }}>
            What compliance teams and CISOs ask.
          </p>
          <p className="mkt-body mkt-body-lg" style={{ maxWidth: "60ch", marginTop: 24, color: "var(--forest-soft)" }}>
            Six questions we hear on nearly every call before Crowther runs in production.
          </p>
        </div>
        <div style={{ marginTop: 56 }}>
          <Accordion items={FAQ} />
        </div>
      </section>

      {/* Latest thinking (Prime Intellect research grid pattern) */}
      <section className="mkt-shell mkt-section" data-testid="mkt-thinking">
        <SectionIndex n="05" label="Latest thinking" />
        <div className="mkt-lede">
          <p className="mkt-h-l" style={{ maxWidth: "22ch" }}>
            What we publish when we can defend it.
          </p>
        </div>
        <div className="mkt-thinking-grid">
          <a href="/enterprise/research" className="mkt-thinking-card" data-testid="mkt-thinking-1">
            <div className="mkt-thinking-cover forest">DeepResearch Bench</div>
            <div className="mkt-thinking-body">
              <div className="mkt-thinking-meta">
                <span className="mkt-thinking-tag">Benchmark</span>
                <span>Jul 2025</span>
              </div>
              <p className="mkt-thinking-title">
                An agent built on Atom placed second overall at 53.70, and first on insight at 57.2, ahead of Gemini 2.5 Pro.
              </p>
            </div>
          </a>
          <a href="/enterprise/blog" className="mkt-thinking-card" data-testid="mkt-thinking-2">
            <div className="mkt-thinking-cover tan">
              Entity resolution as the moat that lasts.
            </div>
            <div className="mkt-thinking-body">
              <div className="mkt-thinking-meta">
                <span className="mkt-thinking-tag">Perspective</span>
                <span>Coming soon</span>
              </div>
              <p className="mkt-thinking-title">
                Why the durable value of a deployment lives in the resolved entities behind the documents, not the documents themselves.
              </p>
            </div>
          </a>
          <a href="/enterprise/blog" className="mkt-thinking-card" data-testid="mkt-thinking-3">
            <div className="mkt-thinking-cover mint">
              Sovereign clouds and the 155-country line.
            </div>
            <div className="mkt-thinking-body">
              <div className="mkt-thinking-meta">
                <span className="mkt-thinking-tag">Perspective</span>
                <span>Coming soon</span>
              </div>
              <p className="mkt-thinking-title">
                The data-residency map is redrawing itself. What it changes for the institutions that must operate inside it.
              </p>
            </div>
          </a>
        </div>
      </section>

      {/* -----------------------------------------------------------------
          COHERE-INSPIRED COLOUR BAND
          Chromatic panels: immersive statement + testimonial + news grid +
          pastoral panel. Controlled by SHOW_COLOUR_BAND flag above; flip to
          false to remove without further edits.
          ----------------------------------------------------------------- */}
      {SHOW_COLOUR_BAND && <CohereBand />}

      {/* Close: see it against your own firm */}
      <section className="mkt-shell mkt-section mkt-section-last" data-testid="mkt-close">
        <SectionIndex n="06" label="Now, against your own firm" />
        <div className="mkt-split">
          <div className="mkt-split-title-col">
            <h2 className="mkt-h-l">See it against your own firm.</h2>
            <p className="mkt-body mkt-split-lede">
              You have read the argument. Your customised AI strategy plan shows where your firm&rsquo;s data is exposed today, and how to bring it home.
            </p>
            <div style={{ marginTop: 32 }}>
              <Link to="/plan" className="mkt-btn mkt-btn-primary" data-testid="mkt-close-cta">
                Get Your Firm&rsquo;s Free Customised AI Roadmap
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            </div>
          </div>
          <div>
            <div className="mkt-label mkt-split-label">What you receive</div>
            <HairlineList rows={CLOSE_ROWS} />
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
